import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { FileText, Download, Calendar, TrendingUp, TrendingDown, ChevronRight, LogOut, Shield, BarChart3, Target, AlertTriangle, Lock, Key, PenLine, Save, CheckCircle, Upload, Activity, Users, Package, Zap, Clock, Lightbulb, ArrowRight, Crosshair, Info, Megaphone, MapPin, Radio, Newspaper, ListChecks, ScrollText, Crown } from 'lucide-react';
import { PositioningChart, RadarPositionChart, ScoreEvolutionChart, LeversChart } from '@/components/Charts';
import { KpiCard } from '@/components/KpiCard';
import { useCabinetReport, CabinetReportBadge, CabinetReportBanner, CabinetReportToast, CabinetUpgradeHint } from '@/components/CabinetReport';
import CampaignsTab from '@/pages/client/CampaignsTab';
import EcosystemTab from '@/pages/client/EcosystemTabV2';
import RadarTab from '@/pages/client/RadarTab';
import VeilleTab from '@/pages/client/VeilleTab';
import ActionsTab from '@/pages/client/ActionsTab';
import VeilleProactivePage from '@/pages/client/veille-proactive/VeilleProactivePage';
import { VeilleProactiveUpsellModal } from '@/pages/client/veille-proactive/UpsellModal';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cdfce993-612e-4617-ac7b-89da32b19bd7/artifacts/wcwsdulx_Logo%20benchory.png";

// Helper to normalize diagnostic items (backend returns dicts or strings)
const aiStr = (item) => {
  if (typeof item === 'string') return item;
  if (Array.isArray(item)) return item.map(aiStr).join(' | ');
  if (typeof item === 'object' && item !== null) {
    const parts = [];
    if (item.titre) parts.push(item.titre);
    for (const k of ['constat', 'detail', 'pourquoi', 'action', 'objectif', 'impact', 'texte', 'description']) {
      if (item[k]) parts.push(String(item[k]));
    }
    if (parts.length > 0) return parts.join(' — ');
    // Fallback: readable dump
    const sections = Object.entries(item).map(([k, v]) => {
      const label = k.replace(/_/g, ' ');
      return Array.isArray(v) ? `${label}: ${v.join(', ')}` : `${label}: ${v}`;
    });
    return sections.join(' | ');
  }
  return String(item);
};

const PHARMA_FIELDS = [
  // Groupe 1: Chiffre d'affaires
  { key: '_group_ca', group: true, label: "Chiffre d'affaires" },
  { key: 'ca_ht', label: "Chiffre d'affaires HT total du mois", unit: '\u20ac', placeholder: '150000', required: true, source: 'LGO' },
  { key: 'ca_remboursable_ht', label: 'Dont CA remboursable HT', unit: '\u20ac', placeholder: '115000', source: 'LGO', indent: true },
  { key: 'ca_parapharmacie', label: 'Dont CA parapharmacie HT', unit: '\u20ac', placeholder: '35000', source: 'LGO', indent: true },
  { key: 'ca_otc', label: 'Dont CA vente libre / OTC HT', unit: '\u20ac', placeholder: '12000', source: 'LGO', indent: true },
  // Groupe 2: Achats & Charges
  { key: '_group_charges', group: true, label: 'Achats & Charges' },
  { key: 'achats_marchandises', label: 'Achats marchandises HT du mois', unit: '\u20ac', placeholder: '98000', required: true, source: 'LGO' },
  { key: 'cout_personnel', label: 'Co\u00fbt total personnel charges incluses', unit: '\u20ac', placeholder: '23000', required: true, source: 'Paie' },
  { key: 'charges_externes', label: 'Charges externes du mois', unit: '\u20ac', placeholder: '14000', required: true, source: 'Comptabilit\u00e9' },
  // Groupe 3: Activite & Ordonnances
  { key: '_group_activite', group: true, label: 'Activit\u00e9 & Ordonnances' },
  { key: 'jours_ouverture', label: "Nombre de jours d'ouverture du mois", unit: 'jours', placeholder: '26', type: 'int', required: true, source: 'Saisie' },
  { key: 'nb_clients', label: 'Nombre de clients / tickets du mois', unit: '', placeholder: '4200', type: 'int', required: true, source: 'LGO' },
  { key: 'nb_ordonnances', label: "Nombre total d'ordonnances d\u00e9livr\u00e9es", unit: '', placeholder: '5500', type: 'int', source: 'LGO' },
  { key: 'nb_ordonnances_renouvellement', label: 'Dont ordonnances en renouvellement', unit: '', placeholder: '3200', type: 'int', source: 'LGO', indent: true },
  { key: 'nb_lignes_dispensees', label: 'Nombre de lignes dispens\u00e9es', unit: '', placeholder: '12000', type: 'int', source: 'LGO' },
  // Groupe 4: Productivite
  { key: '_group_productivite', group: true, label: 'Productivit\u00e9' },
  { key: 'heures_travaillees_equipe', label: "Total heures travaill\u00e9es par l'\u00e9quipe ce mois", unit: 'h', placeholder: '680', source: 'Planning / Paie' },
  // Groupe 5: Stock
  { key: '_group_stock', group: true, label: 'Stock' },
  { key: 'stock_debut_mois_ht', label: 'Valeur du stock en d\u00e9but de mois', unit: '\u20ac', placeholder: '52000', source: 'LGO' },
  { key: 'stock_moyen', label: 'Valeur moyenne du stock sur le mois', unit: '\u20ac', placeholder: '55000', source: 'LGO', help: 'Moyenne entre stock d\u00e9but et stock fin de mois' },
  // Groupe 6: Tresorerie & Tiers Payant
  { key: '_group_tresorerie', group: true, label: 'Tr\u00e9sorerie & Tiers Payant' },
  { key: 'tresorerie_disponible', label: 'Tr\u00e9sorerie disponible en fin de mois', unit: '\u20ac', placeholder: '30000', source: 'Relev\u00e9 bancaire' },
  { key: 'montant_tiers_payant_soumis', label: 'Montant total soumis au tiers payant', unit: '\u20ac', placeholder: '95000', source: 'LGO' },
  { key: 'montant_tiers_payant_rejete', label: 'Dont montant rejet\u00e9 par les caisses', unit: '\u20ac', placeholder: '3200', source: 'LGO', indent: true },
  { key: 'montant_tiers_payant_attente', label: 'Montant en attente de remboursement', unit: '\u20ac', placeholder: '18000', source: 'LGO' },
  // Groupe 7: Donnee contextuelle
  { key: '_group_contexte', group: true, label: 'Donn\u00e9e contextuelle' },
  { key: 'taux_generiques', label: 'Taux de substitution g\u00e9n\u00e9rique', unit: '%', placeholder: '85.0', source: 'LGO', help: 'Donn\u00e9e optionnelle mais recommand\u00e9e' },
];

const RESTO_FIELDS = [
  { key: 'ca_ht', label: "Chiffre d'affaires HT", unit: '€', placeholder: '89000' },
  { key: 'nb_clients', label: 'Nombre de clients (couverts)', unit: '', placeholder: '2340', type: 'int' },
  { key: 'jours_ouverture', label: "Jours d'ouverture du mois", unit: 'jours', placeholder: '26', type: 'int' },
  { key: 'cout_matieres', label: 'Coût matières premières HT', unit: '€', placeholder: '28480' },
  { key: 'cout_personnel', label: 'Coût du personnel', unit: '€', placeholder: '33820' },
  { key: 'stock_moyen', label: 'Valeur moyenne de stock', unit: '€', placeholder: '9200' },
  { key: 'cout_energie', label: 'Coût énergie', unit: '€', placeholder: '3560' },
  { key: 'impots_taxes', label: 'Impôts & taxes', unit: '€', placeholder: '1780' },
  { key: 'commissions_plateformes', label: 'Commissions plateformes', unit: '€', placeholder: '890' },
];

function ClientDataEntry({ myData, dataPeriod, setDataPeriod, dataForm, setDataForm, dataSaving, setDataSaving, dataSaved, setDataSaved, setMyData, questionnaire, setQuestionnaire }) {
  const isPharmacy = myData?.sector_slug === 'pharmacies';
  const fields = isPharmacy ? PHARMA_FIELDS : RESTO_FIELDS;
  const est = myData?.establishment;
  const history = myData?.history || [];

  // Load existing data when period changes
  useEffect(() => {
    const existing = history.find(h => h.period === dataPeriod);
    if (existing) {
      setDataForm(existing.data || {});
      setQuestionnaire(existing.questionnaire || {});
    } else {
      setDataForm({});
      setQuestionnaire({});
    }
  }, [dataPeriod, history, setDataForm, setQuestionnaire]);

  const handleSave = async () => {
    setDataSaving(true);
    try {
      const token = localStorage.getItem('benchory_token');
      const payload = {};
      fields.filter(f => !f.group).forEach(f => {
        const val = dataForm[f.key] || 0;
        payload[f.key] = f.type === 'int' ? parseInt(val) : parseFloat(val);
      });
      await axios.post(`${API_URL}/client/data`, { period: dataPeriod, data: payload, questionnaire }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataSaved(true);
      // Refresh
      const res = await axios.get(`${API_URL}/client/my-data`, { headers: { Authorization: `Bearer ${token}` } });
      setMyData(res.data);
      setTimeout(() => setDataSaved(false), 3000);
    } catch (err) {
      console.error(err);
    }
    setDataSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="client-data-entry">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <PenLine size={18} className="text-[#B8860B]" />
          <h2 className="text-lg font-bold text-[#1B2A4A] font-manrope">Saisie de vos données mensuelles</h2>
        </div>
        <p className="text-sm text-gray-400 mb-5">
          {est ? `${est.name} — ` : ''}Tous les montants sont HT.
          {isPharmacy && est?.employees_fte && <span className="ml-2 font-semibold text-[#1B2A4A]">ETP : {est.employees_fte}</span>}
        </p>

        <div className="flex items-center gap-4 mb-6">
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Période</label>
            <input
              data-testid="client-data-period"
              type="month"
              value={dataPeriod}
              onChange={e => setDataPeriod(e.target.value)}
              className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent"
            />
          </div>
          {dataSaved && (
            <div className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
              <CheckCircle size={16} /> Données enregistrées
            </div>
          )}
        </div>

        {/* ══════════ QUESTIONNAIRE QUALITATIF ══════════ */}
        <div className="bg-[#F8F9FA] rounded-xl border border-gray-100 p-5 mb-6" data-testid="questionnaire-section">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={16} className="text-[#B8860B]" />
            <h3 className="text-sm font-bold text-[#1B2A4A] font-manrope">Votre ressenti du mois</h3>
            <span className="text-[10px] text-gray-400 ml-1">Facultatif — enrichit votre rapport personnalisé</span>
          </div>

          {/* Q1 — Qualité du mois */}
          <div className="mb-5">
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Comment qualifieriez-vous ce mois ?</label>
            <div className="flex flex-wrap gap-2 mb-2" data-testid="q-qualite-mois">
              {[
                { value: 'tres_bon', label: 'Très bon mois', color: 'emerald' },
                { value: 'correct', label: 'Mois correct', color: 'blue' },
                { value: 'difficile', label: 'Mois difficile', color: 'amber' },
                { value: 'tres_difficile', label: 'Très difficile', color: 'red' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setQuestionnaire({ ...questionnaire, qualite_mois: questionnaire.qualite_mois === opt.value ? '' : opt.value })}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                    questionnaire.qualite_mois === opt.value
                      ? opt.color === 'emerald' ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                      : opt.color === 'blue' ? 'bg-blue-100 border-blue-300 text-blue-800'
                      : opt.color === 'amber' ? 'bg-amber-100 border-amber-300 text-amber-800'
                      : 'bg-red-100 border-red-300 text-red-800'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`q-qualite-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Précisez votre ressenti si vous le souhaitez..."
              value={questionnaire.qualite_mois_detail || ''}
              onChange={e => setQuestionnaire({ ...questionnaire, qualite_mois_detail: e.target.value })}
              className="w-full max-w-lg h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent"
              data-testid="q-qualite-detail"
            />
          </div>

          {/* Q2 — Événements exceptionnels */}
          <div className="mb-5">
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Événements exceptionnels ce mois-ci</label>
            <div className="flex flex-wrap gap-2 mb-2" data-testid="q-evenements">
              {[
                "Absence d'un ou plusieurs collaborateurs",
                "Travaux dans ou autour de la pharmacie",
                "Rupture de stock fournisseur importante",
                "Nouvelle concurrence dans la zone",
                "Problème informatique / LGO",
              ].map(evt => {
                const evts = questionnaire.evenements_exceptionnels || [];
                const isSelected = evts.includes(evt);
                return (
                  <button
                    key={evt}
                    type="button"
                    onClick={() => {
                      const updated = isSelected ? evts.filter(e => e !== evt) : [...evts, evt];
                      setQuestionnaire({ ...questionnaire, evenements_exceptionnels: updated });
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      isSelected ? 'bg-[#1B2A4A] border-[#1B2A4A] text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`q-evt-${evt.slice(0, 10).replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {evt}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Autre événement (facultatif)..."
                value={questionnaire.evenements_autre || ''}
                onChange={e => setQuestionnaire({ ...questionnaire, evenements_autre: e.target.value })}
                className="flex-1 max-w-sm h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent"
                data-testid="q-evt-autre"
              />
              {(questionnaire.evenements_autre || '').trim() !== '' && (
                <input
                  type="text"
                  placeholder="Précisez l'impact sur votre activité..."
                  value={questionnaire.evenements_detail || ''}
                  onChange={e => setQuestionnaire({ ...questionnaire, evenements_detail: e.target.value })}
                  className="flex-1 max-w-sm h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent animate-in fade-in duration-200"
                  data-testid="q-evt-detail"
                />
              )}
            </div>
          </div>

          {/* Q3 — Impact zone de chalandise */}
          <div className="mb-5">
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Changements dans votre zone de chalandise</label>
            <div className="flex flex-wrap gap-2 mb-2" data-testid="q-impact-zone">
              {[
                "Médecin absent ou remplacé",
                "Fermeture d'un cabinet prescripteur",
                "Événement local (marché, fête, travaux de voirie...)",
                "Météo inhabituelle (canicule, épisode hivernal...)",
                "Aucun impact particulier",
              ].map(impact => {
                const zones = questionnaire.impact_zone || [];
                const isSelected = zones.includes(impact);
                return (
                  <button
                    key={impact}
                    type="button"
                    onClick={() => {
                      let updated;
                      if (impact === "Aucun impact particulier") {
                        updated = isSelected ? [] : [impact];
                      } else {
                        updated = isSelected
                          ? zones.filter(z => z !== impact)
                          : [...zones.filter(z => z !== "Aucun impact particulier"), impact];
                      }
                      setQuestionnaire({ ...questionnaire, impact_zone: updated });
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      isSelected ? 'bg-[#1B2A4A] border-[#1B2A4A] text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`q-zone-${impact.slice(0, 10).replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {impact}
                  </button>
                );
              })}
            </div>
            {!(questionnaire.impact_zone || []).includes("Aucun impact particulier") && (questionnaire.impact_zone || []).length > 0 && (
              <input
                type="text"
                placeholder="Précisez les changements observés dans votre zone..."
                value={questionnaire.impact_zone_detail || ''}
                onChange={e => setQuestionnaire({ ...questionnaire, impact_zone_detail: e.target.value })}
                className="w-full max-w-lg h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent animate-in fade-in duration-200"
                data-testid="q-zone-detail"
              />
            )}
          </div>

          {/* Q4, Q5, Q6 — Champs texte libres */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Actions menées ce mois-ci</label>
              <textarea
                value={questionnaire.actions_menees || ''}
                onChange={e => setQuestionnaire({ ...questionnaire, actions_menees: e.target.value })}
                placeholder="Ex : Mise en avant gamme solaire, formation équipe sur les génériques..."
                rows={3}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent resize-none"
                data-testid="q-actions-menees"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Un indicateur vous a surpris ?</label>
              <textarea
                value={questionnaire.indicateur_surprenant || ''}
                onChange={e => setQuestionnaire({ ...questionnaire, indicateur_surprenant: e.target.value })}
                placeholder="Ex : Baisse du panier moyen alors que le trafic a augmenté..."
                rows={3}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent resize-none"
                data-testid="q-indicateur-surprenant"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Objectif prioritaire mois prochain</label>
              <textarea
                value={questionnaire.objectif_mois_suivant || ''}
                onChange={e => setQuestionnaire({ ...questionnaire, objectif_mois_suivant: e.target.value })}
                placeholder="Ex : Améliorer le taux de génériques, réduire le stock dormant..."
                rows={3}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent resize-none"
                data-testid="q-objectif-suivant"
              />
            </div>
          </div>
        </div>

        {/* ══════════ INDICATEURS CHIFFR\u00c9S ══════════ */}
        <div className="space-y-6 mb-6">
          {fields.reduce((acc, f) => {
            if (f.group) {
              acc.push({ group: f, fields: [] });
            } else if (acc.length > 0) {
              acc[acc.length - 1].fields.push(f);
            } else {
              if (!acc._ungrouped) { acc._ungrouped = []; }
              acc._ungrouped.push(f);
            }
            return acc;
          }, []).map((section, i) => (
            <div key={i}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-[10px] font-bold text-[#1B2A4A] uppercase tracking-wider whitespace-nowrap">{section.group.label}</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.fields.map(f => (
                  <div key={f.key} className={f.indent ? 'pl-4 border-l-2 border-[#B8860B]/20' : ''}>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                      {f.indent && <span className="text-[#B8860B] mr-1">&rsaquo;</span>}
                      {f.label} {f.unit && <span className="text-[#B8860B]">({f.unit})</span>}
                      {f.required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      data-testid={`client-data-${f.key}`}
                      type="number"
                      value={dataForm[f.key] || ''}
                      onChange={e => setDataForm({ ...dataForm, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent"
                    />
                    <div className="flex items-center gap-2 mt-0.5">
                      {f.help && <p className="text-[10px] text-gray-400">{f.help}</p>}
                      {f.source && <span className="text-[9px] text-gray-300 bg-gray-50 px-1.5 py-0.5 rounded">{f.source}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleSave}
          disabled={dataSaving || !dataForm.ca_ht}
          className="bg-[#1B2A4A] text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-[#152039] transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
          data-testid="client-data-save-btn"
        >
          {dataSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
          Enregistrer les données du mois
        </button>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="font-bold text-[#1B2A4A] font-manrope mb-4">Historique de vos saisies</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Période</th>
                  <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">CA HT</th>
                  {isPharmacy ? (
                    <>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Achats</th>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Personnel</th>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Ordonnances</th>
                    </>
                  ) : (
                    <>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Clients</th>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Matières</th>
                      <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wider py-2 px-3">Personnel</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 12).map(d => (
                  <tr key={d.period} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 px-3 font-medium text-[#1B2A4A]">{d.period}</td>
                    <td className="py-2.5 px-3 text-right">{Number(d.data?.ca_ht || 0).toLocaleString('fr-FR')} €</td>
                    {isPharmacy ? (
                      <>
                        <td className="py-2.5 px-3 text-right">{Number(d.data?.achats_marchandises || 0).toLocaleString('fr-FR')} €</td>
                        <td className="py-2.5 px-3 text-right">{Number(d.data?.cout_personnel || 0).toLocaleString('fr-FR')} €</td>
                        <td className="py-2.5 px-3 text-right">{Number(d.data?.nb_ordonnances || 0).toLocaleString('fr-FR')}</td>
                      </>
                    ) : (
                      <>
                        <td className="py-2.5 px-3 text-right">{Number(d.data?.nb_clients || 0).toLocaleString('fr-FR')}</td>
                        <td className="py-2.5 px-3 text-right">{Number(d.data?.cout_matieres || 0).toLocaleString('fr-FR')} €</td>
                        <td className="py-2.5 px-3 text-right">{Number(d.data?.cout_personnel || 0).toLocaleString('fr-FR')} €</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function ClientFluxCommercial({ ticketAnalysis, setTicketAnalysis, ticketUploading, setTicketUploading, ticketPeriods, setTicketPeriods, dataPeriod }) {
  const [selectedPeriod, setSelectedPeriod] = useState(dataPeriod);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileInputRef = useRef(null);

  const loadAnalysis = async (period) => {
    const token = localStorage.getItem('benchory_token');
    try {
      const res = await axios.get(`${API_URL}/client/tickets/analysis?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setTicketAnalysis(res.data);
    } catch { setTicketAnalysis(null); }
  };

  useEffect(() => { if (selectedPeriod) loadAnalysis(selectedPeriod); }, [selectedPeriod]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTicketUploading(true);
    setUploadMsg('');
    const token = localStorage.getItem('benchory_token');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/client/tickets/import?period=${selectedPeriod}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setUploadMsg(`${res.data.lines_imported} lignes importées (${res.data.tickets_detected} tickets)`);
      await loadAnalysis(selectedPeriod);
      // Refresh periods
      const pRes = await axios.get(`${API_URL}/client/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      if (pRes.data?.periods) setTicketPeriods(pRes.data.periods);
    } catch (err) {
      setUploadMsg(err.response?.data?.detail || 'Erreur lors de l\'import');
    }
    setTicketUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getGaugeColor = (pct) => pct >= 70 ? '#10B981' : pct >= 40 ? '#B8860B' : '#EF4444';
  const kpis = ticketAnalysis?.kpis;

  return (
    <div className="space-y-6" data-testid="client-flux-commercial">
      {/* Import section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <Activity size={18} className="text-[#B8860B]" />
          <h2 className="text-lg font-bold text-[#1B2A4A] font-manrope">Analyse du flux commercial</h2>
        </div>
        <p className="text-sm text-gray-400 mb-5">Importez vos tickets de caisse (CSV ou Excel) pour analyser votre performance commerciale.</p>

        <div className="flex items-end gap-4 flex-wrap mb-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Période</label>
            <input type="month" value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
              className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" data-testid="flux-period" />
          </div>
          <div>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} ref={fileInputRef} className="hidden" data-testid="flux-file-input" />
            <button onClick={() => fileInputRef.current?.click()} disabled={ticketUploading}
              className="bg-[#1B2A4A] text-white px-5 py-2.5 rounded-lg font-bold text-sm hover:bg-[#152039] transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
              data-testid="flux-upload-btn">
              {ticketUploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
              Importer mes tickets
            </button>
          </div>
          {uploadMsg && <p className="text-sm text-emerald-600 font-medium flex items-center gap-1"><CheckCircle size={14} /> {uploadMsg}</p>}
        </div>

        {ticketPeriods.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            <span className="text-xs text-gray-400">Périodes disponibles :</span>
            {ticketPeriods.map(p => (
              <button key={p.period} onClick={() => setSelectedPeriod(p.period)}
                className={`text-xs px-2 py-1 rounded-full transition-all ${selectedPeriod === p.period ? 'bg-[#1B2A4A] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {p.period} ({p.lines} lignes)
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Analysis dashboard */}
      {ticketAnalysis?.has_tickets && kpis && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid="flux-kpi-tickets-h">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tickets / heure</p>
              <p className="text-2xl font-bold text-[#1B2A4A] mt-1">{kpis.tickets_per_hour}</p>
              <p className="text-xs text-gray-400">Densité de trafic</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid="flux-kpi-ca-h">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">CA / heure</p>
              <p className="text-2xl font-bold text-[#1B2A4A] mt-1">{Number(kpis.ca_per_hour).toLocaleString('fr-FR')} €</p>
              <p className="text-xs text-gray-400">Puissance économique</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid="flux-kpi-produits">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Produits / ticket</p>
              <p className="text-2xl font-bold text-[#1B2A4A] mt-1">{kpis.produits_per_ticket}</p>
              <p className="text-xs text-gray-400">Vente additionnelle</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid="flux-kpi-puissance">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Puissance / client</p>
              <p className="text-2xl font-bold text-[#1B2A4A] mt-1">{Number(kpis.puissance_client).toLocaleString('fr-FR')} €</p>
              <p className="text-xs text-gray-400">Valeur commerciale</p>
            </div>
          </div>

          {/* Capacity gauge + potential */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="flux-capacity">
              <h3 className="text-sm font-bold text-[#1B2A4A] mb-4">Utilisation de capacité</h3>
              <div className="flex items-center gap-6">
                <div className="relative w-28 h-28">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke={getGaugeColor(kpis.utilisation_pct)}
                      strokeWidth="3" strokeDasharray={`${kpis.utilisation_pct} ${100 - kpis.utilisation_pct}`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold" style={{ color: getGaugeColor(kpis.utilisation_pct) }}>{kpis.utilisation_pct}%</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-500">Heures creuses : <span className="font-bold text-[#1B2A4A]">{kpis.heures_creuses_count}h</span></p>
                  <p className="text-gray-500">Tickets manquants : <span className="font-bold text-[#1B2A4A]">{Number(kpis.tickets_manquants).toLocaleString('fr-FR')}</span></p>
                  <p className="text-gray-500">Multi-produits : <span className="font-bold text-[#1B2A4A]">{kpis.taux_multi_produits}%</span></p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl border border-red-100 shadow-sm p-6" data-testid="flux-potentiel">
              <h3 className="text-sm font-bold text-red-800 mb-2">CA potentiel inexploité</h3>
              <p className="text-3xl font-bold text-red-600 font-manrope">{Number(kpis.ca_potentiel_inexploite).toLocaleString('fr-FR')} €</p>
              <p className="text-xs text-red-500 mt-1">Estimation capturable (20% du potentiel brut)</p>
              <p className="text-xs text-gray-500 mt-3">Basé sur {ticketAnalysis.summary.etp} ETP et {Number(ticketAnalysis.summary.hrs_mois).toLocaleString('fr-FR')}h/mois</p>
            </div>
          </div>

          {/* Hourly distribution */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="flux-hourly">
            <h3 className="text-sm font-bold text-[#1B2A4A] mb-4">Répartition horaire du trafic</h3>
            <div className="flex items-end gap-1 h-40">
              {Array.from({ length: 14 }, (_, i) => i + 7).map(h => {
                const count = ticketAnalysis.hourly_distribution?.[String(h)] || 0;
                const maxCount = Math.max(1, ...Object.values(ticketAnalysis.hourly_distribution || {}).map(Number));
                const pct = (count / maxCount) * 100;
                const cap = ticketAnalysis.summary.etp * 10;
                const isCreuse = cap > 0 && (count / cap) < 0.4;
                return (
                  <div key={h} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full relative" style={{ height: '120px' }}>
                      <div className="absolute bottom-0 left-0.5 right-0.5 rounded-t transition-all"
                        style={{ height: `${Math.max(4, pct)}%`, backgroundColor: isCreuse ? '#EF4444' : '#1B2A4A' }} />
                    </div>
                    <span className="text-[9px] text-gray-400">{h}h</span>
                    <span className="text-[9px] font-bold text-gray-600">{count}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#1B2A4A]" /> Normal</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500" /> Heure creuse (&lt; 40%)</span>
            </div>
          </div>
        </>
      )}

      {ticketAnalysis && !ticketAnalysis.has_tickets && (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center">
          <Upload size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">Aucune donnée de tickets pour cette période.</p>
          <p className="text-gray-400 text-xs mt-1">Importez un fichier CSV ou Excel de vos tickets de caisse pour activer l'analyse.</p>
        </div>
      )}
    </div>
  );
}


export function ClientReportsList() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [scoreTrend, setScoreTrend] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showVeilleProactiveUpsell, setShowVeilleProactiveUpsell] = useState(false);
  const [myData, setMyData] = useState(null);
  const [dataForm, setDataForm] = useState({});
  const [questionnaire, setQuestionnaire] = useState({});
  const [dataPeriod, setDataPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });
  const [dataSaving, setDataSaving] = useState(false);
  const [dataSaved, setDataSaved] = useState(false);
  const [ticketAnalysis, setTicketAnalysis] = useState(null);
  const [latestReportFull, setLatestReportFull] = useState(null);
  const [ticketUploading, setTicketUploading] = useState(false);
  const [ticketPeriods, setTicketPeriods] = useState([]);
  const [pendingPeriods, setPendingPeriods] = useState([]);
  const [radarAlertCount, setRadarAlertCount] = useState(0);
  const [veilleNewCount, setVeilleNewCount] = useState(0);
  const [actionsTodoCount, setActionsTodoCount] = useState(0);
  const navigate = useNavigate();

  // Cabinet monthly report state (toast / banner / badge / nav routing)
  const cabinet = useCabinetReport(user);
  const [cabinetToastDismissed, setCabinetToastDismissed] = useState(false);
  const openCabinetReport = () => navigate('/espace-client/rapport-mensuel');
  const cabinetBadgeState = !cabinet.isEligible
    ? null
    : !cabinet.available
      ? 'attente'
      : cabinet.consulted ? 'consulte' : 'nouveau';

  useEffect(() => {
    // Lightweight badges polling (counts only) with fallback to last known values
    const token = localStorage.getItem('benchory_token');
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    const fetchBadges = async () => {
      try {
        const [radar, veille, actions] = await Promise.all([
          fetch(`${API_URL}/radar/dashboard`, { headers: h }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API_URL}/veille/news?limit=40`, { headers: h }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API_URL}/actions?status=a_faire`, { headers: h }).then(r => r.ok ? r.json() : null).catch(() => null),
        ]);
        // Only update if we got fresh data (network OK), otherwise keep last known
        if (radar?.alerts) setRadarAlertCount(radar.alerts.filter(a => a.severity !== 'low').length);
        if (veille && typeof veille.new_since_last_visit === 'number') setVeilleNewCount(veille.new_since_last_visit);
        if (actions && actions.by_status) setActionsTodoCount(actions.by_status.a_faire || 0);
      } catch (e) {
        // Silently keep last known counts
      }
    };
    fetchBadges();
    const id = setInterval(fetchBadges, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('benchory_token');
    if (!token) { navigate('/login'); return; }
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API_URL}/auth/me`, { headers }),
      axios.get(`${API_URL}/client/reports`, { headers }),
      axios.get(`${API_URL}/client/score-trend`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/client/alerts`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/client/chart-data`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/client/my-data`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/client/tickets`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/client/pending-periods`, { headers }).catch(() => ({ data: [] })),
    ]).then(([u, r, t, a, c, md, tk, pp]) => {
      setUser(u.data);
      const sorted = r.data.sort((a, b) => b.period.localeCompare(a.period));
      setReports(sorted);
      if (t.data) setScoreTrend(t.data);
      setAlerts(a.data || []);
      if (c.data) setChartData(c.data);
      if (md.data) setMyData(md.data);
      if (tk.data?.periods) setTicketPeriods(tk.data.periods);
      setPendingPeriods(pp.data || []);
      // Load full latest report (with kpi_results)
      if (sorted.length > 0) {
        axios.get(`${API_URL}/client/reports/${sorted[0].id}`, { headers })
          .then(res => setLatestReportFull(res.data))
          .catch(() => {});
      }
      setLoading(false);
    }).catch(() => {
      localStorage.removeItem('benchory_token');
      navigate('/login');
    });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('benchory_token');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) { setPwMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas' }); return; }
    if (pwForm.newPw.length < 6) { setPwMsg({ type: 'error', text: 'Minimum 6 caractères' }); return; }
    setPwLoading(true);
    setPwMsg({ type: '', text: '' });
    try {
      const token = localStorage.getItem('benchory_token');
      await axios.post(`${API_URL}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.newPw }, { headers: { Authorization: `Bearer ${token}` } });
      setPwMsg({ type: 'success', text: 'Mot de passe modifié avec succès' });
      setPwForm({ current: '', newPw: '', confirm: '' });
      setTimeout(() => setShowChangePassword(false), 2000);
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
    setPwLoading(false);
  };

  const dashboardRef = useRef(null);

  const handleDownloadPdf = async (reportId, reportName, reportPeriod) => {
    setDownloading(reportId);
    const token = localStorage.getItem('benchory_token');
    const fileName = `BENCHORY_${(reportName || 'Rapport').replace(/ /g, '_')}_${reportPeriod}.pdf`;

    // Strategy 1: Try visual dashboard capture (html2canvas + jsPDF)
    if (dashboardRef.current) {
      try {
        const container = dashboardRef.current;
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#f8fafc',
          logging: false,
          windowWidth: container.scrollWidth,
          windowHeight: container.scrollHeight,
        });

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const pdfW = 210;
        const pdfH = 297;
        const margin = 8;
        const contentW = pdfW - margin * 2;
        const ratio = contentW / imgWidth;
        const scaledH = imgHeight * ratio;
        const pageContentH = pdfH - margin * 2 - 14;
        const totalPages = Math.ceil(scaledH / pageContentH);
        const pdf = new jsPDF('p', 'mm', 'a4');

        for (let pg = 0; pg < totalPages; pg++) {
          if (pg > 0) pdf.addPage();
          pdf.setFillColor(27, 42, 74);
          pdf.rect(0, 0, pdfW, 10, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7);
          pdf.setTextColor(184, 134, 11);
          pdf.text('BENCHORY', margin, 6.5);
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(6);
          pdf.text(`${reportName || ''}  |  ${reportPeriod}`, pdfW - margin, 6.5, { align: 'right' });

          const srcY = pg * (pageContentH / ratio);
          const srcH = Math.min(pageContentH / ratio, imgHeight - srcY);
          const destH = srcH * ratio;
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = imgWidth;
          pageCanvas.height = Math.ceil(srcH);
          const ctx = pageCanvas.getContext('2d');
          ctx.drawImage(canvas, 0, srcY, imgWidth, srcH, 0, 0, imgWidth, srcH);
          pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', margin, 12, contentW, destH);

          pdf.setFontSize(6);
          pdf.setTextColor(150, 150, 150);
          pdf.text(`Page ${pg + 1}/${totalPages}`, pdfW / 2, pdfH - 4, { align: 'center' });
          pdf.text('benchory.fr', pdfW - margin, pdfH - 4, { align: 'right' });
        }

        pdf.save(fileName);
        setDownloading(null);
        return;
      } catch (captureErr) {
        console.warn('Capture PDF failed, falling back to backend:', captureErr);
      }
    }

    // Strategy 2: Fallback to backend-generated PDF
    try {
      const res = await axios.get(`${API_URL}/reports/${reportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Backend PDF download failed:', err);
      alert('Le téléchargement a échoué. Veuillez réessayer.');
    }
    setDownloading(null);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600 bg-emerald-50';
    if (score >= 45) return 'text-[#B8860B] bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="w-10 h-10 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" />
    </div>
  );

  const latestReport = reports.length > 0 ? reports[0] : null;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="client-portal-page">
      {/* Cabinet Benchory — toast premier login après publication */}
      {cabinet.isEligible && cabinet.available && cabinet.toastDue && !cabinetToastDismissed && (
        <CabinetReportToast
          period={cabinet.period}
          onOpen={() => { cabinet.markToastSeen(); openCabinetReport(); }}
          onDismiss={() => { setCabinetToastDismissed(true); cabinet.markToastSeen(); }}
        />
      )}
      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src={LOGO_URL} alt="BENCHORY" className="h-9" />
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-bold text-[#1B2A4A] font-manrope">{user?.name}</p>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
            <button onClick={() => setShowChangePassword(true)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1B2A4A] transition-all" data-testid="client-change-password-btn" title="Modifier le mot de passe">
              <Key size={18} />
            </button>
            <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1B2A4A] transition-all" data-testid="client-logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </nav>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowChangePassword(false)}>
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="change-password-modal">
            <div className="flex items-center gap-2 mb-4">
              <Lock size={18} className="text-[#B8860B]" />
              <h2 className="text-lg font-bold text-[#1B2A4A] font-manrope">Modifier le mot de passe</h2>
            </div>
            {pwMsg.text && (
              <div className={`px-3 py-2 rounded-lg mb-4 text-sm ${pwMsg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`} data-testid="change-password-msg">
                {pwMsg.text}
              </div>
            )}
            <form onSubmit={handleChangePassword} className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Mot de passe actuel</label>
                <input data-testid="change-pw-current" type="password" value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" required />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Nouveau mot de passe</label>
                <input data-testid="change-pw-new" type="password" value={pwForm.newPw} onChange={e => setPwForm({...pwForm, newPw: e.target.value})}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" required />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Confirmer</label>
                <input data-testid="change-pw-confirm" type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" required />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowChangePassword(false)} className="flex-1 h-9 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all">Annuler</button>
                <button data-testid="change-pw-submit" type="submit" disabled={pwLoading}
                  className="flex-1 h-9 bg-[#1B2A4A] text-white rounded-lg text-sm font-semibold hover:bg-[#152039] transition-all disabled:opacity-50 flex items-center justify-center gap-1">
                  {pwLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Modifier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome banner */}
        <div className="bg-[#1B2A4A] rounded-2xl p-8 text-white mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 opacity-5">
            <div className="absolute top-8 right-8 w-48 h-48 rounded-full border-2 border-white" />
          </div>
          <div className="relative z-10">
            <p className="text-white/50 text-xs uppercase tracking-[0.2em] mb-2">Espace client</p>
            <h1 className="text-2xl font-bold font-manrope mb-2" data-testid="client-welcome">
              Bonjour{user?.prenom ? `, ${user.prenom}` : user?.name ? `, ${user.name.split(' ')[0]}` : ''}
            </h1>
            <p className="text-white/60 text-sm max-w-xl">
              Retrouvez ici l'ensemble de vos rapports de benchmark mensuels. Chaque rapport compare votre établissement à un panel de pairs anonymisés dans votre secteur.
            </p>
            {user?.is_trial && (
              <div className="mt-3 inline-flex items-center gap-2 bg-[#B8860B]/20 border border-[#B8860B]/30 text-[#B8860B] px-4 py-1.5 rounded-full text-xs font-semibold" data-testid="trial-badge">
                <Clock size={14} />
                Essai gratuit Premium — {user.trial_days_remaining} jour{user.trial_days_remaining > 1 ? 's' : ''} restant{user.trial_days_remaining > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Tabs - horizontal scroll on mobile */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit max-w-full overflow-x-auto" data-testid="client-tabs">
          {cabinet.isEligible && (
            <button
              onClick={openCabinetReport}
              className={`px-3 sm:px-5 py-2 rounded-lg text-sm transition-all flex items-center gap-2 whitespace-nowrap text-gray-600 hover:bg-white/60`}
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 500, letterSpacing: '0.01em' }}
              data-testid="tab-cabinet-report"
            >
              <ScrollText size={16} style={{ color: '#8B0028' }} />
              <span style={{ color: '#2A1419' }}>Rapport du mois</span>
              <CabinetReportBadge state={cabinetBadgeState} period={cabinet.period} />
            </button>
          )}
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'dashboard' ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-[#1B2A4A]'}`}
            data-testid="tab-dashboard"
          >
            <BarChart3 size={16} /> Tableau de bord
          </button>
          {/* Veille Proactive — onglet visible pour tous, gating Diagnostic via modale upsell */}
          <button
            onClick={() => {
              if (user?.plan === 'diagnostic') {
                setShowVeilleProactiveUpsell(true);
              } else {
                setActiveTab('veille-proactive');
              }
            }}
            className={`relative px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'veille-proactive'
                ? 'bg-white text-[#8B0028] shadow-sm'
                : user?.plan === 'diagnostic'
                  ? 'text-gray-500 hover:text-[#8B0028] opacity-70'
                  : 'text-gray-500 hover:text-[#8B0028]'
            }`}
            data-testid="tab-veille-proactive"
            title={user?.plan === 'diagnostic' ? 'Veille Proactive — Pilotage et Premium' : undefined}
          >
            <Crown size={16} className={user?.plan === 'diagnostic' ? '' : 'text-[#8B0028]'} /> Veille Proactive
            {user?.plan === 'diagnostic' && <Lock size={12} className="text-gray-400" />}
          </button>
          <button
            onClick={() => setActiveTab('saisie')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'saisie' ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-[#1B2A4A]'}`}
            data-testid="tab-saisie"
          >
            <PenLine size={16} /> Saisie mensuelle
          </button>
          <button
            onClick={() => setActiveTab('flux')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'flux' ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-[#1B2A4A]'}`}
            data-testid="tab-flux"
          >
            <Activity size={16} /> Flux commercial
            {user?.plan === 'diagnostic' && <Lock size={12} className="text-gray-400" />}
          </button>
          <button
            onClick={() => setActiveTab('campagnes')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'campagnes' ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-[#1B2A4A]'}`}
            data-testid="tab-campagnes"
          >
            <Megaphone size={16} /> Campagnes
          </button>
          <button
            onClick={() => setActiveTab('ecosystem')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'ecosystem' ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-[#1B2A4A]'}`}
            data-testid="tab-ecosystem"
          >
            <MapPin size={16} /> Cartographie
          </button>
          <button
            onClick={() => setActiveTab('radar')}
            className={`relative px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'radar' ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-[#1B2A4A]'}`}
            data-testid="tab-radar"
          >
            <Radio size={16} /> Radar sant&eacute;
            {radarAlertCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center benchory-badge-anim" data-testid="radar-alert-badge">
                {radarAlertCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('veille')}
            className={`relative px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'veille' ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-[#1B2A4A]'}`}
            data-testid="tab-veille"
          >
            <Newspaper size={16} /> Veille
            {veilleNewCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#B8860B] text-white text-[10px] font-bold flex items-center justify-center benchory-badge-anim" data-testid="veille-new-badge">
                {veilleNewCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('actions')}
            className={`relative px-3 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'actions' ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-[#1B2A4A]'}`}
            data-testid="tab-actions"
          >
            <ListChecks size={16} /> Mes actions
            {actionsTodoCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center benchory-badge-anim" data-testid="actions-todo-badge">
                {actionsTodoCount}
              </span>
            )}
          </button>
        </div>

        {/* DATA ENTRY TAB */}
        {activeTab === 'saisie' && (
          <ClientDataEntry
            myData={myData}
            dataPeriod={dataPeriod}
            setDataPeriod={setDataPeriod}
            dataForm={dataForm}
            setDataForm={setDataForm}
            dataSaving={dataSaving}
            setDataSaving={setDataSaving}
            dataSaved={dataSaved}
            setDataSaved={setDataSaved}
            setMyData={setMyData}
            questionnaire={questionnaire}
            setQuestionnaire={setQuestionnaire}
          />
        )}

        {/* FLUX COMMERCIAL TAB */}
        {activeTab === 'flux' && (user?.plan && user.plan !== 'diagnostic' ? (
          <ClientFluxCommercial
            ticketAnalysis={ticketAnalysis}
            setTicketAnalysis={setTicketAnalysis}
            ticketUploading={ticketUploading}
            setTicketUploading={setTicketUploading}
            ticketPeriods={ticketPeriods}
            setTicketPeriods={setTicketPeriods}
            dataPeriod={dataPeriod}
          />
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-10 text-center" data-testid="flux-upgrade-cta">
            <div className="w-16 h-16 rounded-full bg-[#B8860B]/10 flex items-center justify-center mx-auto mb-5">
              <Lock size={28} className="text-[#B8860B]" />
            </div>
            <h2 className="text-xl font-bold text-[#1B2A4A] font-manrope mb-2">Module réservé aux offres Pilotage et Premium</h2>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
              L'analyse de flux commercial (import de tickets de caisse, KPIs comportementaux, détection d'heures creuses, potentiel de CA inexploité) est disponible à partir de l'offre Pilotage à 179 € HT/mois.
            </p>
            <div className="bg-[#F8F9FA] rounded-xl p-5 max-w-sm mx-auto mb-6 text-left space-y-2">
              {['Import CSV/Excel de tickets de caisse', 'Analyse du trafic horaire', 'D\u00e9tection des heures creuses', 'Calcul du CA potentiel inexploit\u00e9', 'KPIs : tickets/h, CA/h, vente additionnelle'].map(f => (
                <div key={f} className="flex items-center gap-2 text-sm text-gray-600">
                  <Activity size={14} className="text-[#B8860B] shrink-0" /> {f}
                </div>
              ))}
            </div>
            <a href="mailto:contactbenchory@proton.me?subject=Passage%20%C3%A0%20l%27offre%20Pilotage"
              className="inline-flex items-center gap-2 bg-[#B8860B] text-white px-6 py-3 rounded-lg font-bold text-sm hover:bg-[#9A7009] transition-all shadow-md"
              data-testid="flux-upgrade-btn"
            >
              Passer à l'offre Pilotage
            </a>
          </div>
        ))}

        {/* CAMPAGNES TAB */}
        {activeTab === 'campagnes' && <CampaignsTab />}

        {/* ECOSYSTEM TAB */}
        {activeTab === 'ecosystem' && <EcosystemTab />}

        {/* RADAR TAB */}
        {activeTab === 'radar' && <RadarTab />}

        {/* VEILLE TAB */}
        {activeTab === 'veille' && <VeilleTab />}

        {/* ACTIONS TAB */}
        {activeTab === 'actions' && <ActionsTab />}

        {/* VEILLE PROACTIVE TAB — Premium (Pilotage / Premium) */}
        {activeTab === 'veille-proactive' && (
          <VeilleProactivePage
            onSwitchToImport={() => setActiveTab('saisie')}
            onCompleteProfile={() => setActiveTab('saisie')}
          />
        )}

        {/* Modale upsell Veille Proactive pour comptes Diagnostic */}
        <VeilleProactiveUpsellModal
          open={showVeilleProactiveUpsell}
          onClose={() => setShowVeilleProactiveUpsell(false)}
        />

        {/* DASHBOARD TAB */}
        {activeTab === 'dashboard' && (<>
        {/* Cabinet Benchory — banner premium si rapport non consulté */}
        {cabinet.isEligible && cabinet.available && !cabinet.consulted && (
          <CabinetReportBanner
            period={cabinet.period}
            indice={cabinet.indice}
            onOpen={openCabinetReport}
          />
        )}
        {/* Cabinet Benchory — mention commerciale discrète pour Diagnostic */}
        {!cabinet.isEligible && user?.plan === 'diagnostic' && (
          <CabinetUpgradeHint onContact={() => window.location.href = 'mailto:contactbenchory@proton.me?subject=Passage%20%C3%A0%20l%27offre%20Pilotage'} />
        )}
        {/* Onboarding wizard — first time */}
        {reports.length <= 1 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-6 mb-6" data-testid="client-onboarding">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-[#B8860B]/10 flex items-center justify-center"><Target size={16} className="text-[#B8860B]" /></div>
              <h2 className="font-bold text-[#1B2A4A] font-manrope">Bienvenue chez BENCHORY</h2>
            </div>
            <p className="text-sm text-gray-600 mb-5">Votre espace personnel est pr&ecirc;t. Voici comment tirer le maximum de votre abonnement :</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '1', title: 'Saisissez vos données', desc: 'Chaque mois, renseignez vos indicateurs dans l\'onglet "Saisie mensuelle". Vos données sont transmises à notre équipe.' },
                { step: '2', title: 'Nous analysons et validons', desc: 'Notre équipe génère votre rapport personnalisé, vérifie la cohérence des données et valide l\'analyse avant publication.' },
                { step: '3', title: 'Consultez votre rapport', desc: 'Une fois publié, votre rapport apparaît ici avec vos KPIs, votre positionnement et vos leviers d\'action.' },
              ].map(s => (
                <div key={s.step} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-sm font-bold shrink-0">{s.step}</div>
                  <div>
                    <h3 className="font-bold text-[#1B2A4A] text-sm font-manrope mb-1">{s.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending periods banner */}
        {pendingPeriods.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6" data-testid="client-pending-banner">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-amber-600" />
              <span className="text-sm font-bold text-amber-800">Données en cours de traitement</span>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              Vos données ont bien été transmises. Notre équipe analyse et prépare votre rapport personnalisé. Vous serez notifié dès sa publication.
            </p>
            <div className="flex flex-wrap gap-2">
              {pendingPeriods.map(p => (
                <span key={p.period} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-amber-200 text-xs font-semibold text-amber-700">
                  <Calendar size={12} /> {p.period}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ══════════ DASHBOARD CAPTURE ZONE (for PDF export) ══════════ */}
        <div ref={dashboardRef}>

        {/* Segment info */}
        {latestReport && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6 flex flex-wrap items-center gap-6" data-testid="client-segment-info">
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-[#B8860B]" />
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Segment</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-[#1B2A4A]">
              <span className="font-medium">{latestReport.segment_label}</span>
            </div>
            <div className="text-xs text-gray-400">
              Cohorte : {latestReport.sample_size} établissements comparables
            </div>
            <div className="text-xs text-gray-400">
              Secteur : {latestReport.sector_name}
            </div>
            <div className="ml-auto">
              <button
                onClick={() => handleDownloadPdf(latestReport.id, latestReport.establishment_name, latestReport.period)}
                disabled={downloading === latestReport.id}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B2A4A] text-white text-xs font-bold hover:bg-[#1B2A4A]/90 transition-all disabled:opacity-50"
                data-testid="download-pdf-main-btn"
              >
                {downloading === latestReport.id ? (
                  <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Génération en cours...</>
                ) : (
                  <><Download size={14} /> Télécharger le PDF</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Score trend & money saved */}
        {scoreTrend && scoreTrend.trend.length >= 1 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="client-trend-section">
            {/* Score trend chart */}
            <div className="md:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-[#B8860B]" />
                <span className="text-xs font-bold text-[#B8860B] uppercase tracking-wider">Évolution de votre score</span>
              </div>
              {scoreTrend.trend.length >= 2 ? (
                <div>
                  <svg viewBox="0 0 500 120" className="w-full" style={{ height: '100px' }}>
                    {[0, 25, 50, 75, 100].map((v, i) => (
                      <g key={i}>
                        <line x1="40" y1={100 - v} x2="490" y2={100 - v} stroke="#f0f0f0" strokeWidth="0.5" />
                        <text x="35" y={104 - v} textAnchor="end" fontSize="8" fill="#9CA3AF">{v}</text>
                      </g>
                    ))}
                    <path
                      d={scoreTrend.trend.map((t, i) => {
                        const x = 40 + (i / Math.max(1, scoreTrend.trend.length - 1)) * 450;
                        const y = 100 - t.score;
                        return `${i === 0 ? 'M' : 'L'}${x},${y}`;
                      }).join(' ')}
                      fill="none" stroke="#1B2A4A" strokeWidth="2.5"
                    />
                    {scoreTrend.trend.map((t, i) => {
                      const x = 40 + (i / Math.max(1, scoreTrend.trend.length - 1)) * 450;
                      const y = 100 - t.score;
                      const col = t.score >= 60 ? '#10B981' : t.score >= 40 ? '#B8860B' : '#EF4444';
                      const [, m] = t.period.split('-');
                      const months = { '01': 'J', '02': 'F', '03': 'M', '04': 'A', '05': 'M', '06': 'J', '07': 'J', '08': 'A', '09': 'S', '10': 'O', '11': 'N', '12': 'D' };
                      return (
                        <g key={i}>
                          <circle cx={x} cy={y} r="4" fill={col} stroke="white" strokeWidth="2" />
                          <text x={x} y="115" textAnchor="middle" fontSize="8" fill="#9CA3AF">{months[m]}</text>
                          <text x={x} y={y - 8} textAnchor="middle" fontSize="8" fontWeight="bold" fill="#1B2A4A">{t.score}</text>
                        </g>
                      );
                    })}
                  </svg>
                  <p className="text-[10px] text-gray-400 mt-2 text-center">Score global mois par mois (0-100)</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">Votre tendance sera visible après 2 rapports</p>
              )}
            </div>
            {/* Money saved */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col justify-center items-center text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                <TrendingUp size={24} className="text-emerald-600" />
              </div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Économies identifiées</p>
              <p className="text-3xl font-bold text-emerald-600 font-manrope" data-testid="client-money-saved">
                {scoreTrend.total_saved > 0 ? `${Number(scoreTrend.total_saved).toLocaleString('fr-FR')} €` : '—'}
              </p>
              <p className="text-[10px] text-gray-400 mt-1">depuis votre entrée en relation</p>
              <p className="text-xs text-gray-500 mt-2">
                {scoreTrend.months_active} rapport{scoreTrend.months_active > 1 ? 's' : ''} généré{scoreTrend.months_active > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}

        {/* Drift Alerts */}
        {alerts.length > 0 && (
          <div className="bg-white rounded-xl border border-red-100 shadow-sm p-5 mb-6" data-testid="client-drift-alerts">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={16} className="text-red-500" />
              <span className="text-xs font-bold text-red-600 uppercase tracking-wider">Alertes de dérive</span>
            </div>
            <div className="space-y-2">
              {alerts.slice(0, 4).map((alert, i) => (
                <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${alert.severity === 'critical' ? 'bg-red-50' : 'bg-amber-50'}`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${alert.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#1B2A4A]">{alert.message}</p>
                  </div>
                  <span className={`text-xs font-bold ${alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>
                    -{alert.score_drop} pts
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Interactive Charts */}
        {chartData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {chartData.positioning && chartData.positioning.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 size={16} className="text-[#B8860B]" />
                  <span className="text-xs font-bold text-[#B8860B] uppercase tracking-wider">Positionnement face au marché</span>
                </div>
                <p className="text-[10px] text-gray-400 mb-2">Vos KPIs vs la médiane de votre secteur (ligne = médiane à 50)</p>
                <PositioningChart data={chartData.positioning} />
              </div>
            )}
            {chartData.positioning && chartData.positioning.length >= 3 && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Target size={16} className="text-[#B8860B]" />
                  <span className="text-xs font-bold text-[#B8860B] uppercase tracking-wider">Profil radar</span>
                </div>
                <p className="text-[10px] text-gray-400 mb-2">Vue synthétique de vos zones d'attention</p>
                <RadarPositionChart data={chartData.positioning} />
              </div>
            )}
            {chartData.evolution && chartData.evolution.length >= 2 && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp size={16} className="text-[#B8860B]" />
                  <span className="text-xs font-bold text-[#B8860B] uppercase tracking-wider">Évolution du score</span>
                </div>
                <p className="text-[10px] text-gray-400 mb-2">Progression mensuelle de votre performance</p>
                <ScoreEvolutionChart data={chartData.evolution} />
              </div>
            )}
            {chartData.levers && chartData.levers.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={16} className="text-red-500" />
                  <span className="text-xs font-bold text-red-600 uppercase tracking-wider">Leviers d'amélioration</span>
                </div>
                <p className="text-[10px] text-gray-400 mb-2">Impact en euros par poste — les économies potentielles</p>
                <LeversChart data={chartData.levers} />
              </div>
            )}
          </div>
        )}

        {/* Latest report highlight */}
        {latestReport && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-lg p-6 mb-8" data-testid="client-latest-report">
            <div className="flex items-center gap-2 mb-4">
              <Target size={16} className="text-[#B8860B]" />
              <span className="text-xs font-bold text-[#B8860B] uppercase tracking-wider">Dernier rapport</span>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-[#1B2A4A] font-manrope">{latestReport.period_label}</h2>
                <p className="text-sm text-gray-400 mt-1">{latestReport.establishment_name}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className={`px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(latestReport.diagnostic?.score_global || 0)}`}>
                    {latestReport.diagnostic?.score_global || 0}/100
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Score stratégique</p>
                </div>
                {latestReport.diagnostic?.raw_score !== undefined && (
                  <div className="text-center" data-testid="client-raw-score">
                    <div className="px-3 py-1.5 rounded-full text-sm font-bold bg-gray-100 text-gray-600">
                      {latestReport.diagnostic.raw_score}/100
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">Positionnement</p>
                  </div>
                )}
                {latestReport.diagnostic?.cout_inaction > 0 && (
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Coût d'inaction</p>
                    <p className="text-lg font-bold text-red-600">{Number(latestReport.diagnostic.cout_inaction).toLocaleString('fr-FR')} €<span className="text-xs text-gray-400">/mois</span></p>
                    {latestReport.diagnostic?.cout_inaction_annuel > 0 && (
                      <p className="text-xs text-red-500">soit {Number(latestReport.diagnostic.cout_inaction_annuel).toLocaleString('fr-FR')} €/an</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={() => navigate(`/espace-client/rapport/${latestReport.id}`)}
                className="bg-[#1B2A4A] text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all shadow-md flex items-center gap-2"
                data-testid="client-view-latest-btn"
              >
                <BarChart3 size={16} /> Consulter le rapport
              </button>
              <button
                onClick={() => handleDownloadPdf(latestReport.id, latestReport.establishment_name, latestReport.period)}
                disabled={downloading === latestReport.id}
                className="bg-white text-[#1B2A4A] border border-gray-200 px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-gray-50 transition-all shadow-sm flex items-center gap-2 disabled:opacity-50"
                data-testid="client-download-latest-pdf"
              >
                {downloading === latestReport.id ? <div className="w-4 h-4 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" /> : <Download size={16} />}
                Télécharger PDF
              </button>
            </div>
          </div>
        )}

        {/* ══════════ KPI BENCHMARK CARDS ══════════ */}
        {latestReportFull?.kpi_results && latestReportFull.kpi_results.length > 0 && (
          <div className="mb-8" data-testid="kpi-benchmark-section">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={16} className="text-[#1B2A4A]" />
              <h2 className="text-sm font-bold text-[#1B2A4A] uppercase tracking-wider">Vos indicateurs cl&eacute;s</h2>
              <span className="text-[10px] text-gray-400 ml-2">
                vs {latestReportFull.sample_size || 0} &eacute;tablissements comparables
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {latestReportFull.kpi_results.map((kpi) => (
                <KpiCard key={kpi.key} kpi={kpi} />
              ))}
            </div>
          </div>
        )}

        {/* ══════════ AI DIAGNOSTIC SECTION ══════════ */}
        {latestReport?.diagnostic?.forces && (
          <div className="space-y-4 mb-6" data-testid="ai-diagnostic-section">

            {/* Score de progression V6 */}
            {latestReport.diagnostic?.score_progression && (
              <div className="flex flex-wrap items-center gap-3 mb-2" data-testid="ai-score-progression">
                {latestReport.diagnostic.score_progression.kpis_ameliores > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-700">
                    <TrendingUp size={12} /> {latestReport.diagnostic.score_progression.kpis_ameliores} en progression
                  </span>
                )}
                {latestReport.diagnostic.score_progression.kpis_stables > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-xs font-semibold text-gray-600">
                    {latestReport.diagnostic.score_progression.kpis_stables} stables
                  </span>
                )}
                {latestReport.diagnostic.score_progression.kpis_degrades > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-xs font-semibold text-red-600">
                    <TrendingDown size={12} /> {latestReport.diagnostic.score_progression.kpis_degrades} en recul
                  </span>
                )}
                {latestReport.diagnostic.score_progression.momentum_actif && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/30 text-xs font-bold text-[#B8860B]">
                    Momentum actif
                  </span>
                )}
              </div>
            )}

            {/* Portrait du mois V6 */}
            {latestReport.diagnostic?.portrait_du_mois && (
              <div className="bg-[#1B2A4A]/5 rounded-xl border border-[#1B2A4A]/10 p-4" data-testid="ai-portrait-mois">
                <p className="text-sm font-medium text-[#1B2A4A] leading-relaxed italic">
                  {aiStr(latestReport.diagnostic.portrait_du_mois)}
                </p>
              </div>
            )}

            {/* Commentaire général */}
            {(latestReport.ai_text || latestReport.diagnostic?.commentaire_general) && (
              <div className="bg-[#1B2A4A] rounded-xl p-5 text-white" data-testid="ai-general-comment">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb size={16} className="text-[#B8860B]" />
                  <span className="text-xs font-bold text-[#B8860B] uppercase tracking-[0.15em]">Analyse — {latestReport.period_label}</span>
                </div>
                <p className="text-sm leading-relaxed text-white/90">
                  {latestReport.diagnostic?.commentaire_general || latestReport.ai_text}
                </p>
              </div>
            )}

            {/* Tendances historiques V6 */}
            {latestReport.diagnostic?.tendances_historiques && (
              <div className="bg-white rounded-xl border border-blue-100 shadow-sm p-5" data-testid="ai-tendances">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp size={14} className="text-blue-600" />
                  <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">Tendances sur la période</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{aiStr(latestReport.diagnostic.tendances_historiques)}</p>
              </div>
            )}

            {/* Cohérences croisées V6 */}
            {latestReport.diagnostic?.coherences_croisees && (
              <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5" data-testid="ai-coherences">
                <div className="flex items-center gap-2 mb-3">
                  <Info size={14} className="text-amber-600" />
                  <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Analyse croisée</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{aiStr(latestReport.diagnostic.coherences_croisees)}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Forces */}
              <div className="bg-white rounded-xl border border-emerald-100 shadow-sm p-5" data-testid="ai-forces">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-50 flex items-center justify-center">
                    <CheckCircle size={14} className="text-emerald-600" />
                  </div>
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Vos forces</span>
                </div>
                <div className="space-y-2.5">
                  {(Array.isArray(latestReport.diagnostic.forces) ? latestReport.diagnostic.forces : []).map((f, i) => (
                    <div key={i} className="flex gap-2.5 items-start">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                      <p className="text-sm text-gray-700 leading-relaxed">{aiStr(f)}</p>
                    </div>
                  ))}
                  {!Array.isArray(latestReport.diagnostic.forces) && latestReport.diagnostic.forces && (
                    <div className="flex gap-2.5 items-start">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                      <p className="text-sm text-gray-700 leading-relaxed">{aiStr(latestReport.diagnostic.forces)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Risques */}
              <div className="bg-white rounded-xl border border-red-100 shadow-sm p-5" data-testid="ai-risques">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
                    <AlertTriangle size={14} className="text-red-600" />
                  </div>
                  <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Points de vigilance</span>
                </div>
                <div className="space-y-2.5">
                  {(Array.isArray(latestReport.diagnostic.risques) ? latestReport.diagnostic.risques : []).map((r, i) => (
                    <div key={i} className="flex gap-2.5 items-start">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                      <p className="text-sm text-gray-700 leading-relaxed">{aiStr(r)}</p>
                    </div>
                  ))}
                  {!Array.isArray(latestReport.diagnostic.risques) && latestReport.diagnostic.risques && (
                    <div className="flex gap-2.5 items-start">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                      <p className="text-sm text-gray-700 leading-relaxed">{aiStr(latestReport.diagnostic.risques)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Simulation retour médiane V6 */}
            {latestReport.diagnostic?.simulation_retour_mediane && latestReport.diagnostic.simulation_retour_mediane.kpi_cible_nom && (
              <div className="bg-gradient-to-br from-[#1B2A4A]/[0.03] to-[#B8860B]/[0.05] rounded-xl border border-[#B8860B]/15 shadow-sm p-5" data-testid="ai-simulation">
                <div className="flex items-center gap-2 mb-4">
                  <Target size={14} className="text-[#B8860B]" />
                  <span className="text-xs font-bold text-[#B8860B] uppercase tracking-wider">Simulation — Retour au niveau médian</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="text-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Indicateur</p>
                    <p className="text-sm font-semibold text-[#1B2A4A]">{latestReport.diagnostic.simulation_retour_mediane.kpi_cible_nom}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Votre valeur</p>
                    <p className="text-sm font-semibold text-red-600">{latestReport.diagnostic.simulation_retour_mediane.valeur_actuelle}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Niveau médian</p>
                    <p className="text-sm font-semibold text-emerald-600">{latestReport.diagnostic.simulation_retour_mediane.valeur_mediane}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Gain potentiel</p>
                    <p className="text-sm font-bold text-[#B8860B]">{latestReport.diagnostic.simulation_retour_mediane.gain_si_mediane_annuel}</p>
                  </div>
                </div>
                {latestReport.diagnostic.simulation_retour_mediane.ecart_formule && (
                  <p className="text-sm text-gray-600 leading-relaxed mb-2">{aiStr(latestReport.diagnostic.simulation_retour_mediane.ecart_formule)}</p>
                )}
                {latestReport.diagnostic.simulation_retour_mediane.effet_cascade && (
                  <p className="text-xs text-[#B8860B]/70 mt-2">{aiStr(latestReport.diagnostic.simulation_retour_mediane.effet_cascade)}</p>
                )}
                {latestReport.diagnostic.simulation_retour_mediane.condition_succes && (
                  <p className="text-xs text-gray-400 mt-2 italic">{aiStr(latestReport.diagnostic.simulation_retour_mediane.condition_succes)}</p>
                )}
              </div>
            )}

            {/* Levier prioritaire */}
            {latestReport.diagnostic.levier_prioritaire && (
              <div className="bg-gradient-to-r from-[#B8860B]/5 to-[#B8860B]/10 rounded-xl border border-[#B8860B]/20 p-5" data-testid="ai-levier">
                <div className="flex items-center gap-2 mb-2">
                  <Crosshair size={16} className="text-[#B8860B]" />
                  <span className="text-xs font-bold text-[#B8860B] uppercase tracking-wider">Levier prioritaire du mois</span>
                </div>
                <p className="text-sm font-medium text-[#1B2A4A] leading-relaxed">
                  {aiStr(latestReport.diagnostic.levier_prioritaire)}
                </p>
              </div>
            )}

            {/* Actions 30 jours */}
            {latestReport.diagnostic.actions_30j && Array.isArray(latestReport.diagnostic.actions_30j) && latestReport.diagnostic.actions_30j.length > 0 && (
              <div className="bg-white rounded-xl border-2 border-[#1B2A4A]/10 shadow-sm p-5" data-testid="ai-actions">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-full bg-[#1B2A4A] flex items-center justify-center">
                    <ArrowRight size={14} className="text-white" />
                  </div>
                  <span className="text-xs font-bold text-[#1B2A4A] uppercase tracking-wider">Plan d'action — 30 jours</span>
                </div>
                <div className="space-y-3">
                  {latestReport.diagnostic.actions_30j.map((a, i) => (
                    <div key={i} className="flex gap-3 items-start bg-gray-50 rounded-lg p-3.5">
                      <div className="w-6 h-6 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {i + 1}
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{aiStr(a)}</p>
                    </div>
                  ))}
                </div>
                {latestReport.diagnostic.mesure && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">Comment mesurer le progrès</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{aiStr(latestReport.diagnostic.mesure)}</p>
                  </div>
                )}
              </div>
            )}

            {/* Horizon 3 mois V6 */}
            {latestReport.diagnostic?.horizon_3_mois && (
              <div className="bg-white rounded-xl border border-indigo-100 shadow-sm p-5" data-testid="ai-horizon">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={14} className="text-indigo-600" />
                  <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Projection à 3 mois</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{aiStr(latestReport.diagnostic.horizon_3_mois)}</p>
              </div>
            )}

            {/* A éviter V6 */}
            {latestReport.diagnostic?.a_eviter && Array.isArray(latestReport.diagnostic.a_eviter) && latestReport.diagnostic.a_eviter.length > 0 && latestReport.diagnostic.a_eviter[0] && (
              <div className="bg-white rounded-xl border border-orange-100 shadow-sm p-4" data-testid="ai-a-eviter">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={12} className="text-orange-500" />
                  <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">À éviter</span>
                </div>
                <div className="space-y-1.5">
                  {latestReport.diagnostic.a_eviter.filter(Boolean).map((e, i) => (
                    <p key={i} className="text-sm text-gray-600 leading-relaxed">{aiStr(e)}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Point de vigilance silencieux V6 */}
            {latestReport.diagnostic?.point_de_vigilance_silencieux && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-testid="ai-vigilance">
                <p className="text-xs text-gray-500 leading-relaxed">
                  <span className="font-semibold text-gray-600">Signal faible :</span> {aiStr(latestReport.diagnostic.point_de_vigilance_silencieux)}
                </p>
              </div>
            )}
          </div>
        )}

        </div>
        {/* ══════════ END DASHBOARD CAPTURE ZONE ══════════ */}

        {/* All reports history */}
        <h2 className="text-lg font-bold text-[#1B2A4A] font-manrope mb-4 flex items-center gap-2">
          <Calendar size={18} className="text-[#B8860B]" />
          Historique de vos rapports
        </h2>

        {reports.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <FileText size={40} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Aucun rapport disponible</p>
            <p className="text-gray-400 text-sm mt-1">Votre premier rapport sera disponible ici dès sa génération</p>
          </div>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => (
              <div
                key={r.id}
                className="flex items-center bg-white rounded-xl border border-gray-100 shadow-[0_1px_4px_rgba(0,0,0,0.03)] px-5 py-3 hover:shadow-md transition-all group"
                data-testid={`client-report-${r.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#1B2A4A]/5 flex items-center justify-center mr-4">
                  <FileText size={18} className="text-[#1B2A4A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#1B2A4A] text-sm font-manrope">{r.period_label}</p>
                  <p className="text-xs text-gray-400">{r.segment_label}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`px-2.5 py-1 rounded-full text-xs font-bold ${getScoreColor(r.diagnostic?.score_global || 0)}`}>
                    {r.diagnostic?.score_global || 0}/100
                  </div>
                  {r.diagnostic?.cout_inaction > 0 && (
                    <span className="text-xs font-bold text-red-600 hidden sm:block">{Number(r.diagnostic.cout_inaction).toLocaleString('fr-FR')} €</span>
                  )}
                  <button
                    onClick={() => handleDownloadPdf(r.id, r.establishment_name, r.period)}
                    disabled={downloading === r.id}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1B2A4A] transition-all disabled:opacity-50"
                    data-testid={`client-download-pdf-${r.id}`}
                    title="Télécharger PDF"
                  >
                    {downloading === r.id ? <div className="w-4 h-4 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" /> : <Download size={16} />}
                  </button>
                  <button
                    onClick={() => navigate(`/espace-client/rapport/${r.id}`)}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 group-hover:text-[#B8860B] transition-all flex items-center gap-1"
                    data-testid={`client-view-report-${r.id}`}
                    title="Consulter le rapport"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-xs text-gray-400">BENCHORY — La transparence crée le progrès</p>
        </div>
        </>)}
      </div>
    </div>
  );
}

export default ClientReportsList;
